import { CircularProgress } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { uiEnvironment } from 'apps/sigma2-ui/src/constants/environment';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useUser } from 'apps/sigma2-ui/src/state/user';

const { callbackLoginUri } = uiEnvironment;

export const CallbackLoginPage = () => {
  const location = useLocation();
  const navigate = useNavigateSafe();
  const language = useLanguage();

  const { myUserStatus, loginWithSSO } = useUser({
    onError: (loginError) => {
      let errorText = language.errorGeneric();

      if (loginError.errorType === 'InvalidCredentialsError') {
        errorText = language.authErrorUserNotFound();
      }

      setError(errorText);
      setIsLoading(false);
    },
  });

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { code } = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      code: params.get('code'),
    };
  }, [location.search]);

  useEffect(() => {
    if (!code) {
      return;
    }

    setIsLoading(true);

    loginWithSSO(code, `${callbackLoginUri}`);
  }, [code, loginWithSSO, navigate]);

  useEffect(() => {
    if (myUserStatus === 'logged_in') {
      navigate('/');
      setIsLoading(false);
    }
  }, [myUserStatus, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const errorDescription = params.get('error_description');

    if (errorDescription) {
      const accessDenied = errorDescription.includes('SsoRoleMappingNotFound');
      if (accessDenied) {
        setError(
          'You do not have access to this platform. Please contact your system administrator.',
        );
      } else {
        setError(language.errorGeneric());
      }
    }
  }, [location.search, language]);

  // return a react div that has the word "Loading" centered on the screen.
  return (
    <div className="flex flex-col items-center justify-center h-screen">
      {isLoading && <div className="text-2xl font-bold">Logging in...</div>}
      {error !== null && <div className="text-red-500">{error}</div>}
      {isLoading && (
        <CircularProgress
          color="inherit"
          className="text-secondary"
          size={96}
          thickness={8}
        />
      )}
    </div>
  );
};
